import {
  EventEmitter,
  Injectable,
  NgZone,
  Renderer2,
  RendererFactory2,
} from '@angular/core';
import type { LngLatLike } from 'mapbox-gl';
import {
  CellGeoData,
  GeocoderCache,
} from '../../map/mapbox/services/geocoder-cache';
import { environment } from '../../../environments/environment';
import { ReportsService } from '../../reports/reports.service';
import { HttpClient } from '@angular/common/http';
import { catchError, Observable, of, Subject, Subscription } from 'rxjs';
import { JsonResponse } from '../api/backend-config';
import { finalize, map as rxMap, tap } from 'rxjs/operators';
import { geocoderContextToString } from '../util/geocoder-context-to-string';
import { FeatureCollection } from 'geojson';
import { UserAccessService } from '../../user/access/user-access.service';
import { MapBoxService, TypeOfMap } from '../../map/mapbox/mapbox.service';
import { RadiusSelectionService } from '../../reports/order-report/choose-location/choose-location-map/radius-selection.service';
import {
  LayerStoreService,
  LEVEL_LAYER,
} from '../../map/mapbox/services/layer-store.service';
import { BreakpointObserverService } from './breakpoint-observer.service';
import { UserInterestLocationService } from './user-interest-location.service';
import { GoogleAnalyticsService } from '../../user/google-analytics.service';
import { GeocodingApiService } from './geocoding-api.service';
import {
  isMapboxGeocoderLoaded,
  isMapboxGlLoaded,
} from '../mapbox/mapbox-loader';
import { PopupPlacementService } from '../../map/mapbox/services/popup-placement.service';
import { LevelSelectionService } from '../../map/mapbox/visualization/level-selection/level-selection.service';
import { geocoderResultLevel } from '../util/geocoder-result-level';

declare const mapboxgl: typeof import('mapbox-gl');
declare const MapboxGeocoder: typeof import('@mapbox/mapbox-gl-geocoder');

export type GeocoderSearchHistory = {
  id: string;
  address: string;
  lng: number;
  lat: number;
};

/**
 * How long `reverseGeocode` waits for a `result` event before giving up and
 * letting the caller fall back. Generous: this is a backstop for a geocoder
 * that will never answer, not a latency budget for one that is merely slow.
 */
const REVERSE_GEOCODE_TIMEOUT_MS = 8_000;

/**
 * Where the camera settles on a searched address. Shared by the geocoder
 * result and the history/example list so that picking a place twice - once by
 * typing it, once from the list - lands on the same view.
 *
 * It is also a default rather than an optional argument on purpose: GL JS
 * tests its camera options with `'zoom' in options`, so a literal carrying an
 * explicit `zoom: undefined` is read as "fly to NaN". That produced an ease
 * whose progress was NaN forever - the map never reached `moveend`, so the
 * `idle` handler that turns a flight into a cell selection never ran.
 */
const SEARCH_RESULT_ZOOM = 12;

/**
 * Marks that the onboarding list has been offered once. Per browser rather
 * than per session: it is a nudge for someone who has never used the field,
 * and re-opening it on every visit would be noise.
 */
const ONBOARDING_LIST_SHOWN_KEY = 'MAP_SEARCH_ONBOARDING_SHOWN';

@Injectable({
  providedIn: 'root',
})
export class SearchGeocoderService {
  public readonly geocoderCache = new GeocoderCache();

  private _reverseGeocoder?: MapboxGeocoder;

  /**
   * Built on first read rather than in field position. This service is
   * `providedIn: 'root'`, so a field initializer here would construct a
   * `MapboxGeocoder` - and therefore require the CDN bundles - the moment
   * anything injected the service. See `mapbox-loader.ts` for why that
   * mattered.
   *
   * Returns undefined rather than constructing when the plugin is missing.
   * `MapboxGeocoder` is a `declare const` global, so `new MapboxGeocoder()`
   * before the bundle executes throws a ReferenceError. A live map is not
   * evidence that it has: GL JS and the geocoder are two independent CDN
   * bundles fetched in parallel, and an ad blocker or a failed fetch leaves
   * this one absent for the life of the page while the map works fine.
   */
  public get reverseGeocoder(): MapboxGeocoder | undefined {
    if (!this._reverseGeocoder) {
      if (!isMapboxGeocoderLoaded()) return undefined;
      this._reverseGeocoder = new MapboxGeocoder({
        accessToken: environment.mapbox.accessToken,
        mapboxgl: (window as any).mapboxgl,
        countries: 'us',
        language: 'en',
        reverseGeocode: true,
      });
    }
    return this._reverseGeocoder;
  }

  public searchHistoryList: GeocoderSearchHistory[] = [];

  /**
   * Whether a history fetch has come back yet. An empty list before one has is
   * "not asked", not "none" - and the two have to be told apart before the
   * example places stand in for an empty history.
   */
  public isHistoryLoaded = false;

  /**
   * Whether the onboarding examples should fill the list. They replace an
   * empty history, never sit beside one, and never flash in the window where a
   * signed-in visitor's own places simply have not arrived yet. An anonymous
   * visitor has nothing to wait for, so they show at once.
   */
  public get shouldShowExamples(): boolean {
    if (this.searchHistoryList.length > 0) return false;
    return !this.userService.getIsAuthorized() || this.isHistoryLoaded;
  }

  public isListVisible: boolean = false;

  readonly requestFocus$ = new Subject<void>();

  /**
   * The pending close from `handleBlur`. Held so that a deliberate re-open can
   * cancel it: focus leaving the field and coming straight back is one gesture,
   * and the close queued by the leaving half must not outlive it.
   */
  private _blurTimer?: ReturnType<typeof setTimeout>;

  private _marker?: mapboxgl.Marker;

  /** Lazy for the same reason as `reverseGeocoder` above. */
  public get marker(): mapboxgl.Marker {
    if (!this._marker) {
      if (!isMapboxGlLoaded()) {
        console.error(
          '[mapbox] marker was read before GL JS finished loading - await loadMapboxGl() first'
        );
      }
      this._marker = new mapboxgl.Marker({ color: 'red' });
    }
    return this._marker;
  }
  // Save last search result coords to use instead of marker coords
  public lastSearchedCoordinates: [number, number] | null = null;

  /**
   * The label point a state or county search clicked, which `center` alone
   * cannot tell apart from an address. See `isAreaSearchPoint`.
   */
  private lastAreaSearchCenter: [number, number] | null = null;

  private isSearchOpenedFirstTime = true;

  /**
   * The state the *caller* last asked for, which is not the same thing as
   * whether the control is on the map: the first attach detaches itself and
   * re-adds a tick later (see below), and the search can be closed inside that
   * tick. Without this the queued re-add put the field back under a map that
   * had already gone back to showing the magnifier button, and the two drew on
   * top of each other.
   */
  private isAttachRequested = false;

  private inputEventListeners!: {
    handleClick: () => void;
    handleInput: () => void;
    handleBlur: () => void;
    handleControlButtons: (event: Event) => void;
  };

  private _clearListener?: () => void;
  private _resultListener?: (e: any) => void;
  // One geocoder is attached at a time (see _resultListener), so this holds
  // the focus subscription for the current one. Without dropping the previous
  // one, every re-open stacked another handler on this root service's Subject,
  // each keeping a detached geocoder alive.
  private _focusSub?: Subscription;

  // Suppress auto clear on geocoder detachment
  private suppressClear = false;

  private renderer: Renderer2;

  constructor(
    private reportsService: ReportsService,
    private userService: UserAccessService,
    rendererFactory: RendererFactory2,
    private http: HttpClient,
    private radiusSelectionService: RadiusSelectionService,
    private mapboxService: MapBoxService,
    private layerStore: LayerStoreService,
    private breakpointObserver: BreakpointObserverService,
    private userInterestLocationService: UserInterestLocationService,
    private gaService: GoogleAnalyticsService,
    private geocodingApi: GeocodingApiService,
    private popupPlacement: PopupPlacementService,
    private levelSelection: LevelSelectionService,
    private zone: NgZone
  ) {
    this.renderer = rendererFactory.createRenderer(null, null);
  }

  public addGeocoderSearch(
    map: mapboxgl.Map,
    searchGeocoder: MapboxGeocoder,
    searchClose: EventEmitter<void>,
    type?: TypeOfMap,
    popup?: mapboxgl.Popup
  ): void {
    this.isAttachRequested = true;

    if (!map.hasControl(searchGeocoder)) {
      this.suppressClear = true;

      const position = 'top-left';
      map.addControl(searchGeocoder, position);

      this._resultListener = (e) => {
        this.handleSearchResult(map, e, type);
        // un-focus geocoder input so mobile keyboard hides
        // @ts-ignore
        searchGeocoder._inputEl.blur();
      };
      searchGeocoder.on('result', this._resultListener);

      this._clearListener = () =>
        this.handleSearchClear(searchGeocoder, searchClose);
      searchGeocoder.on('clear', this._clearListener);

      this.handleInputEvents(searchGeocoder, popup);
    }
    // autofocus input after click
    // @ts-ignore
    searchGeocoder._inputEl.focus();
    this._focusSub?.unsubscribe();
    this._focusSub = this.requestFocus$.subscribe(() => {
      // @ts-ignore
      searchGeocoder._inputEl.focus();
      // The user guide nudges the field to say "find a place"; showing what is
      // in the list is the other half of that sentence.
      this.openSearchList(searchGeocoder);
    });

    // Somehow icon replacement doesnt work on first render so here we artificially remove first geocoder and add new one
    if (this.isSearchOpenedFirstTime) {
      this.isSearchOpenedFirstTime = false;

      // `detachGeocoder`, not `removeGeocoderSearch`: this detach is our own
      // bookkeeping, not the caller withdrawing its request, so the intent
      // recorded above has to survive it for the guard below to mean anything.
      this.detachGeocoder(map, searchGeocoder);

      setTimeout(() => {
        if (!this.isAttachRequested) return;
        this.addGeocoderSearch(map, searchGeocoder, searchClose, type, popup);
      }, 0);
    } else if (type === 'default') {
      // Only on the re-add above, never on the throwaway first attach: that one
      // is detached a line later, and opening a list under an input that is
      // about to be replaced would leave it pointing at nothing.
      this.offerOnboardingList(searchGeocoder);
    }

    // Add async wrapper to avoid races
    queueMicrotask(() => {
      this.suppressClear = false;
    });
  }

  /**
   * Flies to a place the visitor picked off a list rather than out of the
   * geocoder's own suggestions, then clicks it so the popup opens - the same
   * ending a typed search has.
   */
  public flyToAddress(
    point: LngLatLike,
    map: mapboxgl.Map,
    zoom: number = SEARCH_RESULT_ZOOM
  ): void {
    // Outside the zone: the map is built outside it, but a camera animation
    // started from in here inherits *this* zone for its whole flight, and
    // GL JS drives that flight with requestAnimationFrame - a patched one,
    // so every frame of it would run application-wide change detection.
    // Offset, not a bare centre: the popup that this flight ends in covers the
    // top-left of the map, so the cell has to land in the band beside it rather
    // than under it. See `getFocusOffset`.
    this.zone.runOutsideAngular(() =>
      map.flyTo({
        center: point,
        zoom: zoom,
        offset: this.popupPlacement.getFocusOffset(map),
      })
    );

    this.marker.setLngLat(point).addTo(map);

    this.mapboxService.map.once('idle', () => {
      this.mapboxService.simulateMapClick(point as [number, number]);
    });

    this.isListVisible = false;
  }

  /**
   * Whether `point` is the one a state or county search clicked for its popup.
   * That is the area's label point - somewhere in the middle of it, not a place
   * anyone picked - so point-based tools should not treat it as a location.
   *
   * Compared in screen pixels because the simulated click goes lng/lat ->
   * pixel -> lng/lat and does not return the exact coordinates it was given.
   */
  public isAreaSearchPoint(point: LngLatLike): boolean {
    if (!this.lastAreaSearchCenter) return false;

    const map = this.mapboxService.map;
    const searched = map.project(this.lastAreaSearchCenter);
    const clicked = map.project(point);

    return Math.hypot(searched.x - clicked.x, searched.y - clicked.y) < 2;
  }

  /**
   * Writes a place picked off the history/example list into the field, so it
   * ends up looking the way a typed search leaves it: the address in the
   * input, the clear button showing, and `inputString` carrying the query the
   * clear handler reads to tell "wipe the text" from "close the search".
   *
   * Deliberately not the plugin's `setInput`, which re-geocodes what it is
   * given: the map has already flown to the place, and a suggestion list
   * dropping over it a moment later asks the visitor to choose it twice.
   */
  public showPickedAddress(geocoder: MapboxGeocoder, address: string): void {
    const plugin = geocoder as any;
    if (!plugin._inputEl) return;

    plugin._inputEl.value = address;
    plugin.inputString = address;
    plugin._typeahead?.clear();
    plugin._showClearButton?.();
  }

  public removeGeocoderSearch(
    map: mapboxgl.Map,
    searchGeocoder: MapboxGeocoder
  ): void {
    this.isAttachRequested = false;
    this.detachGeocoder(map, searchGeocoder);
  }

  /** Takes the control off the map without touching the caller's intent. */
  private detachGeocoder(
    map: mapboxgl.Map,
    searchGeocoder: MapboxGeocoder
  ): void {
    if (this._resultListener) {
      searchGeocoder.off('result', this._resultListener);
    }

    if (this._clearListener) {
      searchGeocoder.off('clear', this._clearListener);
    }

    this._focusSub?.unsubscribe();
    this._focusSub = undefined;
    clearTimeout(this._blurTimer);

    // The list hangs off the field's position, not inside it, so nothing takes
    // it away when the field goes. A style change closes the search without
    // ever passing through `handleSearchClear`, which left the history - or
    // the onboarding examples - painted over the map under no input at all,
    // still flying the camera when clicked.
    this.isListVisible = false;

    const inputEl = (searchGeocoder as any)._inputEl;
    if (this.inputEventListeners && inputEl) {
      const { handleClick, handleInput, handleBlur, handleControlButtons } =
        this.inputEventListeners;

      inputEl.removeEventListener('click', handleClick);
      inputEl.removeEventListener('input', handleInput);
      inputEl.removeEventListener('blur', handleBlur);
      inputEl.removeEventListener('keydown', handleControlButtons);
    }

    // `removeControl` calls `onRemove` whether or not the control is on the
    // map, and the plugin's `onRemove` unlinks its container from a parent it
    // no longer has - a TypeError, not a no-op. Callers now detach on paths
    // that cannot know whether the attach ever happened, so ask first.
    if (map.hasControl(searchGeocoder)) {
      map.removeControl(searchGeocoder);
    }
  }

  private handleInputEvents(searchGeocoder: any, popup?: mapboxgl.Popup): void {
    const inputEl = searchGeocoder._inputEl;

    const handleClick = () => {
      // On input click active map popup should be closed
      if (popup && popup.isOpen()) {
        popup.remove();
      }

      this.openSearchList(searchGeocoder);
    };

    const handleInput = () => {
      this.isListVisible = false;
    };

    const handleBlur = () => {
      // Disable input clear on blur, probably will use it again
      // searchGeocoder.setInput('');

      clearTimeout(this._blurTimer);
      // Delay is needed to still handle click on history item before closing list
      this._blurTimer = setTimeout(() => {
        const focused = document.activeElement as HTMLElement | null;
        // `activeElement` is null while focus is in flight, and the field
        // itself is not somewhere the visitor "went" - a control that focuses
        // the input (the getting-started line does) blurs it first, and
        // closing the list behind that made it flash and vanish unbidden.
        const stillOurs =
          !!focused &&
          (focused === inputEl ||
            !!focused.closest('.search-history-wrapper') ||
            !!focused.closest('.mapboxgl-ctrl-geocoder'));

        if (!stillOurs) {
          this.isListVisible = false;
        }
      }, 200);
    };

    // Artificially set input to blank space so that it wont close upon ctrl, cmd, shift press
    const handleControlButtons = (event: any) => {
      if (
        (event.ctrlKey || event.shiftKey || event.metaKey) &&
        !event.key.match(/^[a-zA-Z0-9]$/)
      ) {
        if (searchGeocoder.inputString === '') {
          searchGeocoder.setInput(' ');
        }

        return;
      }
    };

    inputEl.addEventListener('click', handleClick);
    inputEl.addEventListener('input', handleInput);
    inputEl.addEventListener('blur', handleBlur);
    inputEl.addEventListener('keydown', handleControlButtons);

    // Store references for later removal
    this.inputEventListeners = {
      handleClick,
      handleInput,
      handleBlur,
      handleControlButtons,
    };
  }

  /**
   * Opens the list under an empty input, and refreshes the history behind it
   * for a signed-in visitor. What actually fills the list is the template's
   * business: the visitor's own history when there is any, the onboarding
   * examples when there is not.
   *
   * A no-op once there is something typed - the geocoder's own suggestions
   * own that space.
   */
  public openSearchList(searchGeocoder: any): void {
    if (searchGeocoder.inputString !== '') return;

    // Outranks a close still sitting in the queue behind the blur that this
    // open almost certainly followed.
    clearTimeout(this._blurTimer);
    this.isListVisible = true;
    if (this.userService.getIsAuthorized()) {
      this.updateSearchHistoryList(searchGeocoder);
    }
  }

  /**
   * Opens the list unprompted the first time a visitor meets the field, so the
   * example places in it stand in for the search they do not yet know to run.
   * Skipped once for good, and skipped entirely for anyone whose own history
   * is the better prompt.
   */
  private offerOnboardingList(searchGeocoder: any): void {
    if (this.readOnboardingShown()) return;
    // The map route is retained, so a return to it re-attaches the geocoder
    // with whatever was left in the field; the suggestions own that space.
    if (searchGeocoder.inputString !== '') return;

    // Marked as offered before we know the outcome, so that a signed-in
    // visitor who turns out to have a history does not repeat the fetch below
    // on every visit to the map.
    this.writeOnboardingShown();

    if (!this.userService.getIsAuthorized()) {
      this.isListVisible = true;
      return;
    }

    // A signed-in visitor's history only arrives over the network, and one who
    // has any is not the visitor this exists for - so wait for the answer.
    this.fetchSearchHistory().subscribe((list) => {
      this.searchHistoryList = list;
      // The field can be gone by the time this lands - a style change closes
      // it - and opening a list under an input that is no longer there is the
      // orphan `detachGeocoder` exists to prevent.
      if (list.length === 0 && this.isAttachRequested) {
        this.isListVisible = true;
      }
    });
  }

  // localStorage throws rather than returning null when the browser refuses
  // site data, and a nudge is not worth failing the whole search wiring for.
  private readOnboardingShown(): boolean {
    try {
      return !!localStorage.getItem(ONBOARDING_LIST_SHOWN_KEY);
    } catch {
      return true;
    }
  }

  private writeOnboardingShown(): void {
    try {
      localStorage.setItem(ONBOARDING_LIST_SHOWN_KEY, 'true');
    } catch {}
  }

  public removeSearchItemFromHistory(
    id: string,
    geocoder: MapboxGeocoder
  ): void {
    this.http
      .delete(environment.apiUrl + `geocoding/my?searchId=${id}`)
      .pipe(
        finalize(() => {
          this.updateSearchHistoryList(geocoder);
        })
      )
      .subscribe(),
      catchError((err) => {
        console.error('ERROR removing from search history', err);
        return of(null);
      });
  }

  public saveSearchResult(
    result: CellGeoData
  ): Observable<JsonResponse<CellGeoData>> {
    return this.http.put<JsonResponse<CellGeoData>>(
      environment.apiUrl + 'geocoding',
      {
        address: result.place_name,
        lng: result.center[0],
        lat: result.center[1],
      }
    );
  }

  private updateSearchHistoryList(geocoder: any): void {
    this.fetchSearchHistory().subscribe((list) => {
      this.searchHistoryList = list;
      // Same race as in `offerOnboardingList`: the field may have been taken
      // off the map while this was in flight.
      if (this.isAttachRequested) geocoder._inputEl?.focus();
    });
  }

  /** Empty rather than erroring: a lost history is a shorter list, not a fault. */
  private fetchSearchHistory(): Observable<GeocoderSearchHistory[]> {
    return this.http
      .get<
        JsonResponse<GeocoderSearchHistory[]>
      >(environment.apiUrl + 'geocoding/my')
      .pipe(
        rxMap((data) => data.object ?? []),
        catchError((err) => {
          console.error('ERROR fetching search history', err);
          return of([] as GeocoderSearchHistory[]);
        }),
        // Including the failure above: a history we could not fetch is one the
        // examples should stand in for, not one they wait on forever.
        tap(() => (this.isHistoryLoaded = true))
      );
  }

  private handleSearchResult = (
    map: mapboxgl.Map,
    event: { result: CellGeoData },
    type?: TypeOfMap
  ): void => {
    this.marker.remove();
    const center = event.result.center;
    const ll = mapboxgl.LngLat.convert(center);

    this.saveSearchResult(event.result).subscribe(),
      catchError((err) => {
        console.error('ERROR saving search result', err);
        return of(null);
      });

    // A state or a county is a feature the map draws whole, so searching one
    // selects that feature rather than the 1-mile cell its label point happens
    // to fall in. Main map only: the level tool is what makes it meaningful,
    // and the report and comparison maps pick a point, not an area.
    const areaLevel =
      type === 'default' ? geocoderResultLevel(event.result) : null;

    // No pin on an area: `center` is only a label point, and dropping a marker
    // on it would claim a precision the result does not have.
    if (!areaLevel) {
      this.marker.setLngLat(center).addTo(map);
    }

    this.lastSearchedCoordinates = center;
    this.lastAreaSearchCenter = areaLevel ? center : null;
    this.userInterestLocationService.setExplicit(ll.lat, ll.lng);

    if (areaLevel) {
      // Before the camera moves: the click that follows selects whatever the
      // *active* level layer draws under that pixel, so the state or county
      // polygon has to be the live one by the time the flight settles.
      this.levelSelection.selectLevel(areaLevel);
      this.flyToArea(map, event.result, areaLevel);
    } else {
      // Outside the zone: the map is built outside it, but a camera animation
      // started from in here inherits *this* zone for its whole flight, and
      // GL JS drives that flight with requestAnimationFrame - a patched one,
      // so every frame of it would run application-wide change detection.
      // See `flyToAddress`: on the main map this flight is followed by a popup
      // over the top-left corner, and `getFocusOffset` keeps the result clear
      // of it. It is a no-op for the report and comparison maps.
      this.zone.runOutsideAngular(() =>
        map.flyTo({
          center,
          zoom: SEARCH_RESULT_ZOOM,
          offset: this.popupPlacement.getFocusOffset(map),
        })
      );
    }

    if (type === 'default') {
      this.gaService.logSearchDone('suggestion');
    }

    if (type === 'default') {
      map.once('idle', () => {
        this.mapboxService.simulateMapClick(center);
      });
    }

    if (type === 'report') {
      // Instead of using simulateMapClick(), simulate feature creation without click
      this.mapboxService.removeAllMarkers(type);
      this.reportsService.deselectReportsFeature();

      // Same rule the map click handler follows: only the reports that
      // summarise an area get a circle. Searching an address used to draw one
      // unconditionally, so a `pin` report showed a trade area when you
      // searched and none when you clicked the same spot - with the panel
      // beside it still saying "This address".
      if (this.reportsService.selectedReportUsesRadius) {
        this.radiusSelectionService.renderCircle(ll);
      } else {
        this.radiusSelectionService.hideLayer();
      }

      this.reportsService.isLocationSelected = true;
      // Same record the map click keeps, so a location found by searching
      // survives stepping back to this page as well.
      this.reportsService.setSelectedPoint({ lng: ll.lng, lat: ll.lat });
      this.queryReverseGeocoder(ll);
    }
  };

  /**
   * Frames a searched state or county whole, inside the band of map the popup
   * leaves free, and never closer than the level tool tolerates - flying past
   * that ceiling would swap the polygon we are about to select for the cells
   * underneath it.
   *
   * `cameraForBounds` and then a plain `flyTo`, rather than `fitBounds`:
   * padding handed to a camera *move* is kept by the transform, so every later
   * `getCenter()` and URL sync would inherit a skew that is only true for this
   * one flight.
   */
  private flyToArea(
    map: mapboxgl.Map,
    result: CellGeoData,
    level: LEVEL_LAYER
  ): void {
    const maxZoom = this.layerStore.maxZoomKeepingLevel(
      level,
      this.breakpointObserver.isMobile.getValue()
    );

    // Undefined for a result mapbox knows no extent for, and for a viewport
    // too small to hold the padding below; both fall back to the label point.
    const camera = result.bbox
      ? map.cameraForBounds(result.bbox, {
          padding: this.popupPlacement.getBoundsPadding(map),
          maxZoom,
        })
      : undefined;

    this.zone.runOutsideAngular(() =>
      map.flyTo({
        center: camera?.center ?? result.center,
        zoom: camera?.zoom ?? maxZoom,
        // The padding already centred the area in the free band; only the
        // fallback still needs the camera shifted out from under the popup.
        offset: camera ? [0, 0] : this.popupPlacement.getFocusOffset(map),
      })
    );
  }

  private handleSearchClear = (
    searchGeocoder: MapboxGeocoder,
    searchClose: EventEmitter<void>
  ): void => {
    if (!this.suppressClear) {
      this.lastSearchedCoordinates = null;
      this.lastAreaSearchCenter = null;
    }

    // Clear event is being fired twice, so we needed to workaround with separate string variable that would store input
    this.marker.remove();
    this.isListVisible = false;

    // @ts-ignore
    const inputString = searchGeocoder.inputString.trim();

    if (!inputString || inputString.length === 0) {
      searchClose.emit();
    } else {
      // @ts-ignore
      searchGeocoder.inputString = '';
    }
  };

  public queryReverseGeocoder(coordinates: mapboxgl.LngLat): void {
    this.reverseGeocodeViaPlugin(coordinates.lat, coordinates.lng).then(
      (res) => {
        if (!res) return;
        // If we'll need more info on address like postal code, number of building etc. we can use res.place_name
        this.reportsService.setSelectedLocation(geocoderContextToString(res));
      }
    );
  }

  /**
   * Reverse-geocodes one point, resolving `null` instead of hanging when the
   * geocoder cannot answer. The single place that drives `reverseGeocoder`:
   * getting this wrong is easy and the failure is invisible, so callers get a
   * promise that always settles rather than the raw plugin.
   *
   * Three ways it can fail to emit `result`, all of which used to leave the
   * caller's promise pending forever:
   *  - the plugin never loaded, so there is no geocoder at all;
   *  - it loaded but nothing has called `addTo()` yet. `addTo` is what builds
   *    the plugin's DOM, and `query()` writes to that DOM, so on a detached
   *    geocoder it throws rather than issuing a request. Since the loader made
   *    `addTo` asynchronous this is reachable from a click on a freshly
   *    painted map;
   *  - the geocoding request itself is lost.
   *
   * Named `...ViaPlugin` to keep it distinct from `reverseGeocode` below,
   * which is a plain REST call and needs no Mapbox JS at all. That one is the
   * better mechanism; this exists because its callers consume the plugin's
   * `result` shape and cache, which is more than a rename to unpick.
   */
  public reverseGeocodeViaPlugin(
    lat: number,
    lng: number
  ): Promise<CellGeoData | null> {
    const geocoder = this.reverseGeocoder;
    if (!geocoder) {
      console.error(
        '[mapbox] reverse geocode skipped: the geocoder plugin is not loaded'
      );
      return Promise.resolve(null);
    }

    return new Promise((resolve) => {
      let timer: ReturnType<typeof setTimeout>;

      const settle = (data: CellGeoData | null) => {
        clearTimeout(timer);
        geocoder.off('result', resultHandler);
        resolve(data);
      };

      const resultHandler = (event: any) => settle(event.result);

      timer = setTimeout(() => {
        console.error(
          `[mapbox] reverse geocode for ${lat},${lng} produced no result within ${REVERSE_GEOCODE_TIMEOUT_MS}ms`
        );
        settle(null);
      }, REVERSE_GEOCODE_TIMEOUT_MS);

      // Listener first: `query` is what starts the request, and registering
      // after it would be a race against a cached answer.
      geocoder.on('result', resultHandler);

      try {
        geocoder.query(`${lat},${lng}`);
      } catch (error) {
        console.error('[mapbox] reverse geocode query failed', error);
        settle(null);
      }
    });
  }

  // Both of these are plain REST calls that need no Mapbox JS at all, so they
  // live in GeocodingApiService and callers that want only them (the
  // sales-forecast address input) can inject that instead of this service.
  public forwardGeocode(input: string): Promise<any> {
    return this.geocodingApi.forwardGeocode(input);
  }

  // Takes a plain pair, not a `LngLat`: it only reads `.lng` and `.lat`, and
  // requiring the class would force callers that never touch a map to await
  // the Mapbox CDN bundle. A real `LngLat` still satisfies this.
  public reverseGeocode(coordinates: {
    lng: number;
    lat: number;
  }): Promise<FeatureCollection> {
    return this.geocodingApi.reverseGeocode(coordinates);
  }
}
